import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Scissors,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Palette,
  ArrowRight,
  Ribbon,
  CircleDot,
  Flower,
  Shirt,
  Sofa,
  Leaf,
  Droplet
} from 'lucide-react';
import { toast } from 'sonner';

// Product type options for Tessile e Fibre
const PRODUCT_TYPES = [
  { value: 'cucito', label: 'Cucito e Sartoria', icon: Scissors },
  { value: 'ricamo', label: 'Ricamo', icon: Sparkles },
  { value: 'maglia', label: 'Maglia e Uncinetto', icon: Heart },
  { value: 'macrame', label: 'Macramè', icon: Ribbon },
  { value: 'tessitura', label: 'Tessitura al Telaio', icon: CircleDot },
  { value: 'feltro', label: 'Feltro e Lana Cardata', icon: Flower },
  { value: 'tintura', label: 'Tintura e Stampa su Tessuto', icon: Palette },
  { value: 'altro', label: 'Altro', icon: Scissors },
];

// Technique/Material options
const TECHNIQUE_OPTIONS = [
  { value: 'mano', label: 'Lavorazione interamente a mano' },
  { value: 'telaio', label: 'Telaio manuale' },
  { value: 'uncinetto', label: 'Uncinetto' },
  { value: 'ferri', label: 'Ferri da maglia' },
  { value: 'ricamo_mano', label: 'Ricamo a mano' },
  { value: 'punto_croce', label: 'Punto croce' },
  { value: 'needle_felting', label: 'Needle felting (infeltrimento ad ago)' },
  { value: 'wet_felting', label: 'Wet felting (infeltrimento a umido)' },
  { value: 'batik', label: 'Batik (tintura a cera)' },
  { value: 'block_print', label: 'Block printing (stampa a tampone)' },
  { value: 'tintura_naturale', label: 'Tintura con pigmenti naturali' },
  { value: 'mista', label: 'Tecnica mista' },
  { value: 'non_so', label: 'Non so, vorrei consiglio' },
];

// Material options
const MATERIAL_OPTIONS = [
  { value: 'cotone', label: 'Cotone' },
  { value: 'lino', label: 'Lino' },
  { value: 'seta', label: 'Seta' },
  { value: 'lana', label: 'Lana' },
  { value: 'cashmere', label: 'Cashmere' },
  { value: 'alpaca', label: 'Alpaca' },
  { value: 'mohair', label: 'Mohair' },
  { value: 'canapa', label: 'Canapa' },
  { value: 'juta', label: 'Juta / Corda naturale' },
  { value: 'feltro', label: 'Feltro / Lana cardata' },
  { value: 'misto', label: 'Fibre miste' },
  { value: 'non_so', label: 'Non so, vorrei consiglio' },
];

// Destination/Purpose options
const DESTINATION_OPTIONS = [
  { value: 'indossare', label: 'Da indossare (abbigliamento/accessori)' },
  { value: 'arredo_casa', label: 'Complemento d\'arredo casa' },
  { value: 'decorazione_parete', label: 'Decorazione da parete' },
  { value: 'regalo', label: 'Regalo speciale' },
  { value: 'neonato', label: 'Per neonato/bambino' },
  { value: 'matrimonio', label: 'Per matrimonio/cerimonia' },
  { value: 'natale', label: 'Decorazione natalizia' },
  { value: 'evento', label: 'Per evento speciale' },
  { value: 'collezione', label: 'Per collezione personale' },
  { value: 'altro', label: 'Altro' },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'piccolo', label: 'Piccolo (< 30cm)', description: 'Accessori, amigurumi, piccoli oggetti' },
  { value: 'medio', label: 'Medio (30-80cm)', description: 'Cuscini, borse, capi bambino' },
  { value: 'grande', label: 'Grande (80-150cm)', description: 'Arazzi, coperte, capi adulto' },
  { value: 'extra', label: 'Extra large (> 150cm)', description: 'Tappeti, arazzi grandi, coperte matrimoniali' },
  { value: 'su_misura', label: 'Su misura (specificare)', description: 'Dimensioni personalizzate' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '50-100', label: '€50 - €100', description: 'Piccoli accessori' },
  { value: '100-200', label: '€100 - €200', description: 'Accessori e oggetti medi' },
  { value: '200-400', label: '€200 - €400', description: 'Capi e complementi' },
  { value: '400-700', label: '€400 - €700', description: 'Pezzi elaborati' },
  { value: '700-1000', label: '€700 - €1.000', description: 'Opere complesse' },
  { value: '1000+', label: '€1.000+', description: 'Creazioni d\'alta gamma' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Con calma' },
  { value: '3months', label: '3+ Mesi', description: 'Progetti complessi' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example textile projects for showcase
const TEXTILE_EXAMPLES = [
  {
    idea: "Maglione oversize con motivo nordico personalizzato",
    result: "Maglione in lana merino lavorato ai ferri con pattern jacquard personalizzato, colori scelti dal cliente, taglia su misura",
    image: "https://images.unsplash.com/photo-1584992236310-6edddc08acff?w=400&h=300&fit=crop",
    category: "Maglia"
  },
  {
    idea: "Arazzo macramè per parete del soggiorno",
    result: "Arazzo 120x80cm in corda di cotone naturale, design geometrico con frange, montato su bastone in legno di deriva",
    image: "https://images.unsplash.com/photo-1619808799783-db68de98fbe0?w=400&h=300&fit=crop",
    category: "Macramè"
  },
  {
    idea: "Corredo neonato ricamato con nome e data",
    result: "Set completo: copertina in cotone biologico, bavaglini e lenzuolino con ricamo a mano del nome, data di nascita e piccoli animali",
    image: "https://images.unsplash.com/photo-1568288796918-03e7d93306bd?w=400&h=300&fit=crop",
    category: "Ricamo"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho commissionato un plaid in alpaca per il mio divano. La qualità è incredibile, morbidissimo e caldo. L'artigiana ha seguito ogni mia indicazione sui colori. Lo adoro!",
    author: "Francesca M.",
    role: "Interior Designer",
    rating: 5
  },
  {
    text: "Volevo un amigurumi del mio gatto per regalarlo a mia figlia. Il risultato è stato commovente: identico al nostro micio! Un ricordo prezioso fatto con amore.",
    author: "Roberto L.",
    role: "Papà soddisfatto",
    rating: 5
  },
  {
    text: "L'arazzo per il mio studio è un'opera d'arte. Ogni ospite mi chiede dove l'ho preso. Quando dico che è fatto a mano su mia richiesta, restano senza parole.",
    author: "Giulia T.",
    role: "Architetto",
    rating: 5
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Quanto tempo ci vuole per realizzare un capo a maglia o uncinetto?",
    answer: "Dipende dalla complessità: un accessorio semplice richiede 1-2 settimane, un maglione 3-4 settimane, una coperta anche 6-8 settimane. L'artigiano ti darà una stima precisa dopo aver valutato il progetto."
  },
  {
    question: "Posso scegliere i colori e i filati?",
    answer: "Assolutamente sì! Puoi indicare i colori desiderati e l'artigiano ti proporrà i filati più adatti. Molti artigiani inviano campioni di filato prima di iniziare per farti scegliere."
  },
  {
    question: "Come funziona per le taglie su misura?",
    answer: "Per capi di abbigliamento, l'artigiano ti chiederà le misure precise (busto, lunghezza maniche, ecc.) o una taglia di riferimento. Alcuni offrono anche prove intermedie per capi complessi."
  },
  {
    question: "I tessuti tinti naturalmente scoloriscono?",
    answer: "Le tinture naturali possono sbiadire leggermente nel tempo, fa parte del loro fascino. L'artigiano ti fornirà istruzioni per il lavaggio che preservano i colori il più a lungo possibile."
  },
  {
    question: "Posso richiedere riproduzioni di tecniche tradizionali regionali?",
    answer: "Certamente! Molti artigiani sono specializzati in tecniche tradizionali italiane come il tombolo, il filet, il puncetto o la tessitura sarda. Specifica la tecnica desiderata nella richiesta."
  }
];

export default function TessileRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    product_type: '',
    technique: '',
    material: '',
    destination: '',
    size: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Tessile e Fibre su Misura | Creazioni Artigianali | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 file');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
        toast.error('Formato non supportato. Usa immagini o PDF');
        continue;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File troppo grande (max 10MB)');
        continue;
      }
      
      try {
        const fileName = `tessile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi la tua idea tessile');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_tessile_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const productLabel = PRODUCT_TYPES.find(t => t.value === formData.product_type)?.label || '';
      const techniqueLabel = TECHNIQUE_OPTIONS.find(t => t.value === formData.technique)?.label || '';
      const materialLabel = MATERIAL_OPTIONS.find(t => t.value === formData.material)?.label || '';
      const destinationLabel = DESTINATION_OPTIONS.find(d => d.value === formData.destination)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(s => s.value === formData.size)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (productLabel) details.push(`🧵  Tipo: ${productLabel}`);
      if (techniqueLabel) details.push(`✨  Tecnica: ${techniqueLabel}`);
      if (materialLabel) details.push(`🧶  Materiale: ${materialLabel}`);
      if (destinationLabel) details.push(`🏠  Destinazione: ${destinationLabel}`);
      if (sizeLabel) details.push(`📐  Dimensioni: ${sizeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA TESSILE\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${productLabel}`.trim() || 'Creazione tessile su misura',
        description: fullDescription,
        category: 'Tessile e Fibre',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artigiani.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('tessile-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Tessile e Fibre su Misura | Cucito, Ricamo, Maglia Artigianale | Rarifex</title>
        <meta name="description" content="Creazioni tessili artigianali su misura Made in Italy: cucito, ricamo a mano, maglia e uncinetto, macramè, tessitura, feltro. Richiedi un preventivo gratuito." />
        <meta name="keywords" content="tessile artigianale, ricamo a mano, maglia su misura, uncinetto artigianale, macramè, tessitura telaio, feltro lana cardata, amigurumi, arazzo" />
        <link rel="canonical" href="https://www.rarifex.com/tessile-su-misura" />
        <meta property="og:title" content="Tessile e Fibre su Misura | Creazioni Artigianali Made in Italy | Rarifex" />
        <meta property="og:description" content="Maglia, ricamo, macramè e altre creazioni tessili fatte a mano da artigiani italiani. Preventivi gratuiti." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/tessile-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-rose-50/50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Scissors className="w-4 h-4" />
                    Tessile e Fibre Made in Italy
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Creazioni tessili{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-amber-600">
                      fatte a mano per te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Artigiani italiani creano maglia, ricamo, macramè e tessuti unici secondo i tuoi desideri.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Dalla lana ai fili di seta, dal telaio all&apos;uncinetto:
                    <br />
                    <strong className="text-slate-700">ogni fibra racconta una storia unica.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-rose-500" />
                      <span>Preventivi in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-amber-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="tessile-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del progetto
                        </Label>
                        <Input
                          placeholder="Es: Maglione norvegese, Arazzo per soggiorno..."
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="tessile-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi la tua idea *
                        </Label>
                        <Textarea
                          placeholder="Racconta cosa vorresti... Colori, stile, utilizzo, qualsiasi dettaglio che possa aiutare l'artigiano a capire la tua visione"
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[100px]"
                          required
                          data-testid="tessile-description-textarea"
                        />
                      </div>
                      
                      {/* Product Type & Technique */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo creazione</Label>
                          <select
                            value={formData.product_type}
                            onChange={(e) => handleInputChange('product_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="tessile-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {PRODUCT_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tecnica</Label>
                          <select
                            value={formData.technique}
                            onChange={(e) => handleInputChange('technique', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="tessile-technique-select"
                          >
                            <option value="">Seleziona...</option>
                            {TECHNIQUE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Material & Destination */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Materiale</Label>
                          <select
                            value={formData.material}
                            onChange={(e) => handleInputChange('material', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="tessile-material-select"
                          >
                            <option value="">Seleziona...</option>
                            {MATERIAL_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Destinazione</Label>
                          <select
                            value={formData.destination}
                            onChange={(e) => handleInputChange('destination', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="tessile-destination-select"
                          >
                            <option value="">Seleziona...</option>
                            {DESTINATION_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Size & Budget */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Dimensioni</Label>
                          <select
                            value={formData.size}
                            onChange={(e) => handleInputChange('size', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="tessile-size-select"
                          >
                            <option value="">Seleziona...</option>
                            {SIZE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="tessile-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Tempistiche</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          data-testid="tessile-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* File Upload */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di riferimento (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-rose-400 hover:bg-rose-50/50 transition-colors">
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="text-sm text-slate-500">
                              {uploadingFile ? 'Caricamento...' : 'Carica immagini (max 5)'}
                            </span>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              multiple
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={uploadingFile}
                            />
                          </label>
                        </div>
                        
                        {formData.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.attachments.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Riferimento ${index + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-rose-600 to-amber-600 hover:from-rose-700 hover:to-amber-700 text-white py-6 text-lg font-semibold shadow-lg"
                        data-testid="tessile-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Richiedi Preventivi Gratuiti
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-center text-slate-500">
                        Riceverai proposte da artigiani verificati entro 24-48 ore
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come Funziona
              </h2>
              <p className="text-lg text-slate-600">
                In 4 semplici passi avrai la tua creazione tessile unica
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Raccontaci la tua idea tessile' },
                { icon: Users, title: 'Ricevi', desc: 'Proposte da artigiani specializzati' },
                { icon: Star, title: 'Scegli', desc: 'L\'artigiano e il preventivo migliore' },
                { icon: Heart, title: 'Ammira', desc: 'La tua creazione prende forma' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-rose-100 to-amber-100 flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-rose-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT WE OFFER */}
        <section className="py-16 bg-gradient-to-b from-white to-rose-50/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Puoi Richiedere
              </h2>
              <p className="text-lg text-slate-600">
                I nostri artigiani creano ogni tipo di opera tessile
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {[
                { icon: Scissors, label: 'Cucito e Sartoria', color: 'bg-rose-100 text-rose-600' },
                { icon: Sparkles, label: 'Ricamo a Mano', color: 'bg-purple-100 text-purple-600' },
                { icon: Heart, label: 'Maglia e Uncinetto', color: 'bg-pink-100 text-pink-600' },
                { icon: Ribbon, label: 'Macramè', color: 'bg-amber-100 text-amber-600' },
                { icon: CircleDot, label: 'Tessitura', color: 'bg-orange-100 text-orange-600' },
                { icon: Flower, label: 'Feltro e Lana', color: 'bg-green-100 text-green-600' },
                { icon: Palette, label: 'Tintura Naturale', color: 'bg-blue-100 text-blue-600' },
                { icon: Shirt, label: 'Capi su Misura', color: 'bg-indigo-100 text-indigo-600' },
              ].map((item, index) => (
                <div key={index} className={`${item.color} rounded-xl p-4 text-center`}>
                  <item.icon className="w-8 h-8 mx-auto mb-2" />
                  <p className="font-medium text-sm">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Dalla Richiesta alla Creazione
              </h2>
              <p className="text-lg text-slate-600">
                Esempi di progetti realizzati dai nostri artigiani
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {TEXTILE_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img
                    src={example.image}
                    alt={example.result}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5">
                    <span className="inline-block px-3 py-1 bg-rose-100 text-rose-800 text-xs font-medium rounded-full mb-3">
                      {example.category}
                    </span>
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Richiesta:</strong> {example.idea}
                    </p>
                    <p className="text-sm text-slate-800">
                      <strong>Risultato:</strong> {example.result}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-gradient-to-b from-rose-50/30 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Dicono i Nostri Clienti
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">&ldquo;{testimonial.text}&rdquo;</p>
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500">{testimonial.role}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                Domande Frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-5 flex justify-between items-center">
                      <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 bg-gradient-to-r from-rose-600 to-amber-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Dai vita alla tua idea tessile
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Artigiani italiani pronti a creare per te
            </p>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-rose-600 hover:bg-rose-50 font-semibold shadow-xl px-8 py-6 text-lg"
              data-testid="tessile-final-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Inizia Ora - È Gratis
            </Button>
          </div>
        </section>
        
        {/* Auth Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Accedi per continuare
              </h3>
              <p className="text-slate-600 mb-6">
                Per inviare la tua richiesta e ricevere proposte dagli artigiani, accedi o crea un account.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-rose-600 hover:bg-rose-700"
                  onClick={() => navigate('/login?redirect=/tessile-su-misura')}
                >
                  Accedi
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
